import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as AddIcon } from 'assets/icons/addIcon.svg';
import { CreateButton } from 'features/documentCategories/components/DocumentCategories/styled';
import { useAuth, useWindowSize } from 'hooks';
import { Links } from 'settings';
import theme from 'styles/theme';
import { TaskStatus } from 'types';

import { TasksList } from '../../components';
import { ContainerW, ListsW, PageHeaderW, PageTitle, SeeAllLink } from './styled';

const PROFILE_PAGE_TASKS_LIST_LIMIT = 3;

const TITLE_MOBILE_VISIBLE_SX = { '@media (max-width: 1023px)': { display: 'block' } };

export function ProfileTasks(): ReactElement {
  const { pathname } = useLocation();
  const { employeeId = '' } = useParams<string>();

  const { isMobile } = useWindowSize();
  const { user, isManager } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isProfilePage = pathname.includes(Links.profile.index);
  const isUserViewingHisDetails = employeeId === user?.id;
  const isManagerViewingOthersProfile = isManager && employeeId !== user?.id;
  const withAssigneeParam =
    (!isProfilePage && !isUserViewingHisDetails) || isManagerViewingOthersProfile;

  const viewAllLink = Links.profile.taskManagement.index;

  const taskSections = [
    { status: TaskStatus.to_do, title: t('tasks.statuses.toDo') },
    { status: TaskStatus.in_progress, title: t('tasks.statuses.inProgress') },
    { status: TaskStatus.to_approve, title: t('tasks.statuses.toApprove') },
  ];

  const assigneeIdState = isManagerViewingOthersProfile ? { assigneeId: employeeId } : {};
  const addTaskLink = isProfilePage ? Links.profile.createTask : Links.resources.createTask;

  return (
    <ContainerW>
      <PageHeaderW>
        <PageTitle>{t('tasks.myTasks')}</PageTitle>
        <CreateButton
          startIcon={<AddIcon style={{ fill: theme.palette.white.main }} />}
          onClick={() => navigate(addTaskLink, { state: assigneeIdState })}
          style={{
            height: '56px',
            padding: '16px 32px',
            fontWeight: 400,
            fontSize: '14px',
            backgroundColor: theme.palette.info.main,
            color: theme.palette.common.white,
            borderRadius: '8px',
            textTransform: 'none',
            ...(isMobile && { width: '100%', marginBottom: '16px' }),
          }}
        >
          {t('tasks.addNewTask')}
        </CreateButton>
      </PageHeaderW>
      <ListsW>
        {taskSections.map(({ status, title }) => (
          <TasksList
            key={status}
            status={status}
            title={title}
            limit={PROFILE_PAGE_TASKS_LIST_LIMIT}
            {...(withAssigneeParam && { assigned: employeeId })}
            isProfile
            newTaskAssigneeId={isManagerViewingOthersProfile ? employeeId : undefined}
            viewAllLink={viewAllLink}
            listWrapperSx={{ width: '100%' }}
            titleSx={TITLE_MOBILE_VISIBLE_SX}
          />
        ))}
      </ListsW>
      <SeeAllLink to={Links.profile.taskManagement.index}>{t('we.taskPreview.seeAll')}</SeeAllLink>
    </ContainerW>
  );
}
